import { Autocomplete, Button, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useNotification } from "providers/Notification";
import { ListInput } from "components/ListInput";
import { findOption, optionValuesEqual, renderOptionWithHighlight, searchTextField, sortOptionsAlphabetically } from "utils/searchableOptions";


interface AddNotificationProps {
    initialValue?: number
    notificationTypes: { [key: string]: string }
    defaultEmailRecipient: string
    defaultSlackChannel: string
    emailRecipients: string[]
    slackChannels: string[]
    onBackButtonClicked?: () => void
    onCancel?: () => void
    onAdded: (notificationType: number, values: string[], reset: () => void) => void
}

export default function AddNotification(props: AddNotificationProps) {
    const notification = useNotification();
    const [type, setType] = useState(0);
    const [values, setValues] = useState([] as string[]);

    const [validationError, setValidationError] = useState(false);
    const [editing, setEditing] = useState(false);


    const reset = () => {
        setType(0);
        setValues([]);

        setEditing(false);
    };


    useEffect(() => {
        if (!props.initialValue) {
            reset();
            return;
        }

        setType(props.initialValue);

        if (props.notificationTypes[props.initialValue] === "Slack") {
            setValues(props.slackChannels);
        }

        if (props.notificationTypes[props.initialValue] === "Email") {
            setValues(props.emailRecipients);
        }

        setEditing(true);
    }, [props.initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = () => {
        if (type === 0) {
            notification.Display({
                type: "error",
                title: "Invalid parameters",
                message: "Filter type cannot be empty"
            });
            setValidationError(true);
            return;
        }

        props.onAdded(type, values, reset);
    };


    const typeOptions = sortOptionsAlphabetically(
        Object.entries(props.notificationTypes).map(([key, value]) => ({ value: Number(key), label: value }))
    );

    return <Stack sx={{ paddingTop: "10px" }}>
        {!editing ? <>
            <Button
                id="add-button"
                variant="outlined"
                size="medium"
                fullWidth
                onClick={() => {
                    reset();
                    setEditing(true);
                }}
            >
                Add
            </Button>
        </> : <>
            <Autocomplete
                fullWidth
                disableClearable={true as boolean}
                sx={{ marginTop: "20px" }}
                id="filter-type-select"
                options={typeOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={optionValuesEqual}
                value={findOption(typeOptions, type === 0 ? null : type)}
                onChange={(_, option) => {
                    const value = option?.value || 0;
                    setType(value);

                    if (value === 1 && props.defaultEmailRecipient) {
                        setValues([props.defaultEmailRecipient]);
                        return;
                    }

                    if (value === 2 && props.defaultSlackChannel) {
                        setValues([props.defaultSlackChannel]);
                        return;
                    }

                    setValues([]);
                }}
                renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(typeOptions, type === 0 ? null : type)?.label)}
                renderInput={(params) => searchTextField(params, { label: "Type", error: validationError })}
            />

            {["Slack", "Email"].includes(props.notificationTypes[type]) && <ListInput
                id="values-select"
                label={props.notificationTypes[type] === "Slack" ? "Slack Channels" : "Email Recipients"}
                placeholder="Press ENTER to confirm"
                value={values}
                delimiters={[]}
                onChange={x => setValues(x)}
                onDelete={x => setValues(values.filter(y => y !== x))}
            />
            }

            <Box display="flex" flexGrow={1} alignItems={"end"} marginBottom="15px" marginTop="20px">
                <Button
                    id="cancel-button"
                    variant="text"
                    size="medium"
                    fullWidth
                    onClick={() => {
                        reset();
                        props.onCancel && props.onCancel();
                    }}
                >
                    Cancel
                </Button>
                <Button
                    id="save-button"
                    variant="outlined"
                    size="medium"
                    fullWidth
                    onClick={save}
                >
                    Save
                </Button>
            </Box>
        </>}

    </Stack>;
}